import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { DataSourceConfig } from '../../models/dynamic-form.model';

export interface DataLoadOptions {
  formValue?: any;
  instanceIndex?: number;
  forceRefresh?: boolean;
}

export interface CacheEntry {
  data: any[];
  timestamp: number;
  ttl: number;
}

@Injectable({
  providedIn: 'root',
})
export class FieldDataService {
  private readonly dataCache = new Map<string, CacheEntry>();
  private readonly activeRequests = new Map<string, Observable<any[]>>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes default cache TTL

  /**
   * Load data for a field based on its configuration
   */
  loadData(
    fieldId: string,
    config: DataSourceConfig,
    options: DataLoadOptions = {}
  ): Observable<any[]> {
    // Handle static data
    if (config.type === 'static') {
      return this.handleStaticData(config, options.formValue, options.instanceIndex);
    }

    // Handle service-based data
    if (config.type === 'service' && config.serviceMethod) {
      return this.handleServiceData(fieldId, config, options);
    }

    // Return empty array if no valid configuration
    return of([]);
  }

  /**
   * Handle static data with optional filtering
   */
  private handleStaticData(
    config: DataSourceConfig,
    formValue?: any,
    instanceIndex?: number
  ): Observable<any[]> {
    let data = config.data || [];

    // Apply filter if provided
    if (config.filterFn) {
      data = config.filterFn(data, formValue, instanceIndex);
    }

    return of(data);
  }

  /**
   * Handle service-based data with caching
   */
  private handleServiceData(
    fieldId: string,
    config: DataSourceConfig,
    options: DataLoadOptions
  ): Observable<any[]> {
    // Safety check
    if (!config.serviceMethod) {
      console.error(`No serviceMethod defined for field ${fieldId}`);
      return of([]);
    }

    const cacheKey = this.getCacheKey(fieldId, config, options.formValue, options.instanceIndex);

    // Check if we should use cache
    if (config.cache !== false && !options.forceRefresh) {
      const cachedData = this.getCachedData(cacheKey);
      if (cachedData) {
        return of(cachedData);
      }
    }

    // Check if there's already an active request for this data
    const activeRequest = this.activeRequests.get(cacheKey);
    if (activeRequest) {
      return activeRequest;
    }

    // Make the service call
    const request$ = config.serviceMethod(options.formValue, options.instanceIndex).pipe(
      map((response) => {
        // Transform the response if transform function is provided
        let data = config.transformFn ? config.transformFn(response) : response;

        // Apply filtering if filter function is provided
        if (config.filterFn) {
          data = config.filterFn(data, options.formValue, options.instanceIndex);
        }

        // Cache the data if caching is enabled
        if (config.cache !== false) {
          this.setCachedData(cacheKey, data);
        }

        // Remove from active requests
        this.activeRequests.delete(cacheKey);

        return data;
      }),
      catchError((error) => {
        // Remove from active requests on error
        this.activeRequests.delete(cacheKey);

        console.error(`Error loading data for field ${fieldId}:`, error);

        // Return empty array on error, or throw if you want to handle errors upstream
        return of([]);
      }),
      shareReplay(1) // Share the observable among multiple subscribers
    );

    // Store as active request
    this.activeRequests.set(cacheKey, request$);

    return request$;
  }

  /**
   * Generate a unique cache key based on field and dependencies
   */
  private getCacheKey(
    fieldId: string,
    config: DataSourceConfig,
    formValue?: any,
    instanceIndex?: number
  ): string {
    const keyParts = [fieldId];

    // Add instance index if provided
    if (instanceIndex !== undefined) {
      keyParts.push(`idx:${instanceIndex}`);
    }

    // Add dependent field values to the key
    if (config.dependsOnFields && formValue) {
      const dependentValues = config.dependsOnFields
        .map((field) => `${field}:${formValue[field] ?? 'null'}`)
        .join('|');
      keyParts.push(dependentValues);
    }

    return keyParts.join('-');
  }

  /**
   * Get cached data if it exists and is still valid
   */
  private getCachedData(key: string): any[] | null {
    const entry = this.dataCache.get(key);

    if (!entry) {
      return null;
    }

    // Check if cache has expired
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.dataCache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set cached data with TTL
   */
  private setCachedData(key: string, data: any[], ttl?: number): void {
    this.dataCache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl ?? this.defaultTTL,
    });
  }

  /**
   * Clear cache for a specific field or all fields
   */
  clearCache(fieldId?: string): void {
    if (fieldId) {
      // Clear cache entries for specific field
      const keysToDelete: string[] = [];
      this.dataCache.forEach((_, key) => {
        if (key.startsWith(fieldId)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach((key) => this.dataCache.delete(key));
    } else {
      // Clear entire cache
      this.dataCache.clear();
    }

    // Also clear any active requests
    if (fieldId) {
      const requestKeysToDelete: string[] = [];
      this.activeRequests.forEach((_, key) => {
        if (key.startsWith(fieldId)) {
          requestKeysToDelete.push(key);
        }
      });
      requestKeysToDelete.forEach((key) => this.activeRequests.delete(key));
    } else {
      this.activeRequests.clear();
    }
  }

  /**
   * Clear cache for fields that depend on a specific field
   */
  clearDependentCache(fieldName: string, configs: Map<string, DataSourceConfig>): void {
    configs.forEach((config, fieldId) => {
      if (config.dependsOnFields?.includes(fieldName)) {
        this.clearCache(fieldId);
      }
    });
  }

  /**
   * Preload data for multiple fields
   */
  preloadData(
    fields: Array<{ fieldId: string; config: DataSourceConfig }>,
    formValue?: any
  ): Observable<Map<string, any[]>> {
    const loadRequests = fields.map(({ fieldId, config }) =>
      this.loadData(fieldId, config, { formValue }).pipe(map((data) => ({ fieldId, data })))
    );

    return new Observable((observer) => {
      const results = new Map<string, any[]>();
      let completed = 0;

      loadRequests.forEach((request$) => {
        request$.subscribe({
          next: ({ fieldId, data }) => {
            results.set(fieldId, data);
            completed++;

            if (completed === loadRequests.length) {
              observer.next(results);
              observer.complete();
            }
          },
          error: (error) => {
            observer.error(error);
          },
        });
      });
    });
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): {
    totalEntries: number;
    cacheKeys: string[];
    activeRequests: string[];
    totalSize: number;
  } {
    let totalSize = 0;
    this.dataCache.forEach((entry) => {
      totalSize += JSON.stringify(entry.data).length;
    });

    return {
      totalEntries: this.dataCache.size,
      cacheKeys: Array.from(this.dataCache.keys()),
      activeRequests: Array.from(this.activeRequests.keys()),
      totalSize,
    };
  }

  /**
   * Set default cache TTL
   */
  setDefaultTTL(ttl: number): void {
    this.defaultTTL = ttl;
  }

  /**
   * Clean expired cache entries
   */
  cleanExpiredCache(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.dataCache.forEach((entry, key) => {
      if (now - entry.timestamp > entry.ttl) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.dataCache.delete(key));
  }
}
